import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { dataExportRequestLocationExport, dataExportQueueChamberFluxWideExport } from '../../../api';
import { toLocalStartIso, toLocalEndIso, isValidDateStr } from '../../../utils/dateUtils';
import type { SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind } from '../../../api';
import type { TrackedJob } from './ExportJobsBar';

type ExportPreviewPanelProps = {
	startDate: string;
	endDate: string;
	siteSerialNumber: string | null;
	siteDisplayName: string | null;
	selectedLocationIds: Set<number>;
	selectedSensorIds: Set<number>;
	dataTypes: Set<SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind>;
	measureGroups?: string[];
	selectedHasGradientFlux: boolean;
	onJobQueued: (job: TrackedJob) => void;
	chamberMode: boolean;
	selectedChannels: number[];
	onChannelsChange: (channels: number[]) => void;
};

function formatDateRange(start: string, end: string) {
	const fmt = (d: string) => {
		const parts = d.split('-');
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		return `${months[parseInt(parts[1] ?? '1') - 1]} ${parseInt(parts[2] ?? '1')}, ${parts[0]}`;
	};
	return `${fmt(start)} – ${fmt(end)}`;
}

function ChannelChipInput({
	channels,
	onChange,
}: {
	channels: number[];
	onChange: (channels: number[]) => void;
}) {
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const addChannels = useCallback((raw: string) => {
		const nums = raw
			.split(/[\s,;]+/)
			.map(s => parseInt(s, 10))
			.filter(n => !isNaN(n) && n > 0 && !channels.includes(n));
		if (nums.length > 0) {
			onChange([...channels, ...nums].sort((a, b) => a - b));
		}
		setInputValue('');
	}, [channels, onChange]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
			e.preventDefault();
			if (inputValue.trim()) {
				addChannels(inputValue);
			}
		} else if (e.key === 'Backspace' && inputValue === '' && channels.length > 0) {
			onChange(channels.slice(0, -1));
		}
	};

	const handleBlur = () => {
		if (inputValue.trim()) {
			addChannels(inputValue);
		}
	};

	return (
		<div
			className="min-h-[2.5rem] flex flex-wrap gap-1.5 items-center px-2 py-1.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-smartfield focus-within:border-transparent cursor-text"
			onClick={() => inputRef.current?.focus()}
		>
			{channels.map(n => (
				<span
					key={n}
					className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium"
				>
					{n}
					<button
						type="button"
						onClick={e => { e.stopPropagation(); onChange(channels.filter(c => c !== n)); }}
						className="hover:text-green-900 leading-none"
						aria-label={`Remove chamber ${n}`}
					>
						×
					</button>
				</span>
			))}
			<input
				ref={inputRef}
				type="text"
				value={inputValue}
				onChange={e => setInputValue(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				placeholder={channels.length === 0 ? 'e.g. 5, 15, 17, 26' : ''}
				className="flex-1 min-w-[6rem] outline-none text-sm bg-transparent text-gray-900 placeholder-gray-400"
			/>
		</div>
	);
}

export function ExportPreviewPanel({
	startDate,
	endDate,
	siteSerialNumber,
	siteDisplayName,
	selectedLocationIds,
	selectedSensorIds,
	dataTypes: selectedDataTypes,
	measureGroups,
	selectedHasGradientFlux,
	onJobQueued,
	chamberMode,
	selectedChannels,
	onChannelsChange,
}: ExportPreviewPanelProps) {
	const [exporting, setExporting] = useState(false);
	const dataTypes = measureGroups?.length === 0
		? new Set(Array.from(selectedDataTypes).filter(kind => kind !== 'Manual'))
		: selectedDataTypes;

	const plotCount = selectedLocationIds.size;
	const sensorCount = selectedSensorIds.size;
	const hasSelection = chamberMode
		? selectedChannels.length > 0
		: (plotCount > 0 || sensorCount > 0);
	const gradientFluxOnlyNoTower = !chamberMode
		&& dataTypes.has('GradientFlux')
		&& dataTypes.size === 1
		&& plotCount > 0
		&& !selectedHasGradientFlux;
	const canExport = hasSelection && siteSerialNumber != null && (chamberMode || dataTypes.size > 0) && !gradientFluxOnlyNoTower;

	async function handleExport() {
		if (!canExport || !siteSerialNumber || !isValidDateStr(startDate) || !isValidDateStr(endDate)) {
			return;
		}

		setExporting(true);
		try {
			const startIso = toLocalStartIso(startDate);
			const endIso = toLocalEndIso(endDate);

			if (chamberMode) {
				const result = await dataExportQueueChamberFluxWideExport({
					siteCode: siteSerialNumber,
					startTime: startIso,
					endTime: endIso,
					channels: selectedChannels,
				});
				if (result.jobId) {
					const sitePart = siteDisplayName ?? siteSerialNumber;
					const label = `${sitePart} · chambers ${selectedChannels.join(', ')} · ${formatDateRange(startDate, endDate)}`;
					onJobQueued({
						jobId: result.jobId,
						label,
						state: 'Pending',
						downloadUrls: null,
						queuePosition: null,
						errorMessage: null,
						startedAt: result.actualStartTime ?? null,
					});
					toast.success("Chamber flux export queued — track progress in the jobs bar.");
				} else {
					toast.success("Chamber flux export queued — you'll receive an email when it's ready.");
				}
				return;
			}

			const result = await dataExportRequestLocationExport({
				siteSerialNumber,
				requestBody: {
					startTime: startIso,
					endTime: endIso,
					locationIds: plotCount > 0 ? Array.from(selectedLocationIds) : undefined,
					sensorIds: sensorCount > 0 ? Array.from(selectedSensorIds) : undefined,
					kinds: Array.from(dataTypes),
					measureGroups: dataTypes.has('Manual') ? measureGroups : undefined,
				},
			});

			if (result.jobId) {
				const sitePart = siteDisplayName ?? siteSerialNumber;
				const label = `${sitePart} · ${formatDateRange(startDate, endDate)}`;
				onJobQueued({
					jobId: result.jobId,
					label,
					state: 'Pending',
					downloadUrls: null,
					queuePosition: null,
					errorMessage: null,
					startedAt: result.actualStartTime ?? null,
				});
			} else {
				toast.success("Export queued — you'll receive an email with a download link when it's ready.");
			}
		} catch (err) {
			toast.error('Failed to queue export.');
			console.error(err);
		} finally {
			setExporting(false);
		}
	}

	return (
		<div className="bg-white rounded-lg shadow p-4 space-y-4">
			<h3 className="font-semibold text-gray-900 text-sm">Export</h3>

			{chamberMode ? (
				<>
					<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
						<dt className="text-gray-500">Site</dt>
						<dd className="text-gray-900">{siteDisplayName ?? siteSerialNumber ?? <span className="text-gray-400 italic">none</span>}</dd>
						<dt className="text-gray-500">Period</dt>
						<dd className="text-gray-900">{formatDateRange(startDate, endDate)}</dd>
					</dl>
					<div>
						<label className="block text-xs font-medium text-gray-700 mb-1">Chamber numbers</label>
						<ChannelChipInput channels={selectedChannels} onChange={onChannelsChange} />
						<p className="text-xs text-gray-400 mt-1">Type numbers and press Enter or comma to add. Exports wide-format chamber flux CSV.</p>
					</div>
					{selectedChannels.length === 0 && (
						<p className="text-xs text-amber-600">Enter at least one chamber number to export.</p>
					)}
				</>
			) : (
				<>
					<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
						<dt className="text-gray-500">Site</dt>
						<dd className="text-gray-900">{siteDisplayName ?? siteSerialNumber ?? <span className="text-gray-400 italic">none</span>}</dd>

						<dt className="text-gray-500">Period</dt>
						<dd className="text-gray-900">{formatDateRange(startDate, endDate)}</dd>

						<dt className="text-gray-500">Plots</dt>
						<dd className="text-gray-900">
							{plotCount > 0 ? `${plotCount} selected` : <span className="text-gray-400">none</span>}
						</dd>

						<dt className="text-gray-500">Sensors</dt>
						<dd className="text-gray-900">
							{sensorCount > 0 ? `${sensorCount} selected` : <span className="text-gray-400">none</span>}
						</dd>

						<dt className="text-gray-500">Kinds</dt>
						<dd className="text-gray-900">
							{dataTypes.size > 0 ? Array.from(dataTypes).join(', ') : <span className="text-gray-400">none</span>}
						</dd>
					</dl>

					{!hasSelection && siteSerialNumber && (
						<p className="text-xs text-amber-600">Select plots or sensors above to enable export.</p>
					)}
					{hasSelection && gradientFluxOnlyNoTower && (
						<p className="text-xs text-amber-600">None of the selected plots are linked to a tower — tower flux export would contain only gap-filled rows. Select a plot with a tower link, or uncheck Tower Flux.</p>
					)}
				</>
			)}

			<button
				onClick={handleExport}
				disabled={!canExport || exporting}
				className="w-full py-2.5 px-4 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{exporting ? 'Queuing…' : chamberMode ? 'Export chamber flux CSV' : 'Export CSVs'}
			</button>
		</div>
	);
}
